import { useEffect } from 'react';
import { App, Form, Input, Modal, Typography } from 'antd';
import { useCreateMdm, useUpdateMdm } from '@/hooks/useMdms';
import type { CreateMdmRequest, MdmDto, UpdateMdmRequest } from '@/types/mdm';

const { Text } = Typography;

interface Props {
  open: boolean;
  mdm: MdmDto | null;
  onClose: () => void;
}

export function MdmFormModal({ open, mdm, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const createMutation = useCreateMdm();
  const updateMutation = useUpdateMdm();

  const isEditing = mdm != null;
  const loading = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (open) {
      if (mdm) {
        form.setFieldsValue({
          name: mdm.name,
          code: mdm.code,
          description: mdm.description,
          googleProjectId: mdm.googleProjectId,
          googleCredentialsPath: mdm.googleCredentialsPath,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, mdm, form]);

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (isEditing && mdm) {
        const request: UpdateMdmRequest = {
          name: values.name,
          description: values.description || undefined,
          googleProjectId: values.googleProjectId || undefined,
          googleCredentialsPath: values.googleCredentialsPath || undefined,
        };
        await updateMutation.mutateAsync({ id: mdm.id, request });
        message.success('MDM actualizado.');
      } else {
        const request: CreateMdmRequest = {
          name: values.name,
          code: (values.code as string).toUpperCase(),
          description: values.description || undefined,
          googleProjectId: values.googleProjectId || undefined,
          googleCredentialsPath: values.googleCredentialsPath || undefined,
        };
        await createMutation.mutateAsync(request);
        message.success('MDM creado.');
      }

      onClose();
    } catch (err: unknown) {
      if (err instanceof Error && err.message) {
        message.error(err.message);
      }
    }
  }

  return (
    <Modal
      open={open}
      title={isEditing ? `Editar MDM: ${mdm?.name}` : 'Nuevo MDM'}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isEditing ? 'Guardar' : 'Crear'}
      cancelText="Cancelar"
      width={680}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="name"
          label="Nombre del MDM"
          rules={[{ required: true, message: 'Requerido' }, { max: 200 }]}
          extra={<Text type="secondary" style={{ fontSize: 12 }}>Ej: Global Connect Cloud Workspace</Text>}
        >
          <Input placeholder="Global Connect Cloud Workspace" />
        </Form.Item>

        <Form.Item
          name="code"
          label="Codigo (unico)"
          rules={[
            { required: true, message: 'Requerido' },
            { max: 50 },
            { pattern: /^[A-Z0-9_-]+$/i, message: 'Solo letras, numeros, guion y underscore' },
          ]}
          extra={
            isEditing
              ? <Text type="warning" style={{ fontSize: 12 }}>No editable.</Text>
              : <Text type="secondary" style={{ fontSize: 12 }}>Ej: GLOBALCONNECT, LEGACY. Se convierte a mayusculas.</Text>
          }
        >
          <Input disabled={isEditing} placeholder="GLOBALCONNECT" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Descripcion"
          rules={[{ max: 500 }]}
        >
          <Input.TextArea rows={2} placeholder="Que clientes/uso cubre este MDM" />
        </Form.Item>

        {/* Google Cloud */}
        <Form.Item label={<Text strong>Configuracion Google Cloud</Text>}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Estas credenciales se heredan a todos los Enterprises de este MDM,
            salvo que el Enterprise tenga su propio override.
          </Text>
        </Form.Item>

        <Form.Item
          name="googleProjectId"
          label="Google Project ID"
          rules={[{ max: 100 }]}
          extra={<Text type="secondary" style={{ fontSize: 12 }}>Ej: mdm-global-connect, mdm-platform-494413</Text>}
        >
          <Input placeholder="mdm-global-connect" />
        </Form.Item>

        <Form.Item
          name="googleCredentialsPath"
          label="Path al JSON del Service Account"
          rules={[{ max: 500 }]}
          extra={<Text type="secondary" style={{ fontSize: 12 }}>Path absoluto en el server.</Text>}
        >
          <Input placeholder="D:\OSCAR\Proyectos\MDM PLATFORM\Credenciales\xxx.json" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
